/**
 * Params Manager - Handles clearing and refreshing URL parameters
 * Use this when the modal/iframe is reopened with fresh params
 */

import { store } from '@/Redux/reduxStore';
import { resetIframeParams, setIframeParams } from '@/Redux/slices/iframeParamsSlice';
import { getDynatraceLogger } from '@/Services';
import { resetInitialDimensionsFlag, resetIframeReadyFlag } from '@/Utils/iframe-utils';

export type ThemeMode = 'light' | 'dark';
export type BrandType = 'standard' | 'lf';

export interface IframeParams {
  theme: ThemeMode;
  brand: BrandType;
  tenantId: string | null;
  clientId: string | null;
  premiumSourceUrl: string | null;
}

/**
 * Clear all cached params and state
 * Call this when the modal reopens to ensure fresh data
 */
export const clearCachedParams = () => {
  // Clear Redux state
  store.dispatch(resetIframeParams());
  
  // Reset iframe notification flags to allow fresh resize on reopen
  resetInitialDimensionsFlag();
  resetIframeReadyFlag();
  
  // Clear sessionStorage (params backup)
  sessionStorage.removeItem('clientId');
  sessionStorage.removeItem('tenantId');
  sessionStorage.removeItem('theme');
  sessionStorage.removeItem('sourceUrl');
  
  // Clear any other session data you might have
  sessionStorage.removeItem('withdrawalData');
  sessionStorage.removeItem('selectedMethod');
};

/**
 * Read fresh params from URL and backup to sessionStorage
 */
export const readAndBackupParams = (envBrand: BrandType, isLFBrand: boolean): IframeParams => {
  const params = new URLSearchParams(window.location.search);
  const themeParam = params.get('theme');
  const tenantIdParam = params.get('tenantId');
  const clientIdParam = params.get('clientId') || params.get('punterId');
  const sourceUrlParam = params.get('sourceUrl');
  
  const iframeData: IframeParams = {
    theme: (themeParam === 'dark' || themeParam === 'light') ? themeParam as ThemeMode : (isLFBrand ? 'dark' : 'light'),
    brand: envBrand,
    tenantId: tenantIdParam,
    clientId: clientIdParam,
    premiumSourceUrl: sourceUrlParam,
  };
  
  // Backup to sessionStorage for recovery
  if (clientIdParam) sessionStorage.setItem('clientId', clientIdParam);
  if (tenantIdParam) sessionStorage.setItem('tenantId', tenantIdParam);
  if (sourceUrlParam) sessionStorage.setItem('sourceUrl', sourceUrlParam);
  sessionStorage.setItem('theme', iframeData.theme);
  
  return iframeData;
};

/**
 * Initialize params from URL and store in Redux
 */
export const initializeParams = (envBrand: BrandType, isLFBrand: boolean): IframeParams => {
  const iframeData = readAndBackupParams(envBrand, isLFBrand);
  store.dispatch(setIframeParams(iframeData));
  return iframeData;
};

/**
 * Get current params from URL
 */
export const getCurrentUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    clientId: params.get('clientId') || params.get('punterId'),
    tenantId: params.get('tenantId'),
    theme: params.get('theme'),
    sourceUrl: params.get('sourceUrl'),
  };
};

/**
 * Check if URL params have changed compared to cached params
 */
export const haveParamsChanged = (
  currentClientId: string | null, 
  currentTenantId: string | null,
  currentSourceUrl: string | null,
  cachedClientId: string | null,
  cachedTenantId: string | null,
  cachedSourceUrl: string | null
): boolean => {
  return (
    (!!currentClientId && currentClientId !== cachedClientId) ||
    (!!currentTenantId && currentTenantId !== cachedTenantId) ||
    (!!currentSourceUrl && currentSourceUrl !== cachedSourceUrl)
  );
};

/**
 * Refresh params when URL changes (modal reopened with different user)
 */
export const refreshParamsIfChanged = (
  envBrand: BrandType,
  isLFBrand: boolean,
  cachedClientId: string | null,
  cachedTenantId: string | null,
  cachedSourceUrl: string | null
): IframeParams | null => {
  const current = getCurrentUrlParams();
  
  if (haveParamsChanged(current.clientId, current.tenantId, current.sourceUrl, cachedClientId, cachedTenantId, cachedSourceUrl)) {
    clearCachedParams();
    return initializeParams(envBrand, isLFBrand);
  }
  
  return null;
};

/**
 * Check for stale/missing params and attempt recovery
 */
export const checkAndRecoverParams = (cachedClientId: string | null): void => {
  const current = getCurrentUrlParams();
  
  // If URL params are missing but we have cached values, params were lost
  if (!current.clientId && cachedClientId) {
    const cachedClientIdFromStorage = sessionStorage.getItem('clientId');
    
    if (!cachedClientIdFromStorage) {
      const logger = getDynatraceLogger();
      logger?.logError('Cannot recover params - modal should be closed and reopened');
    }
  }
};

/**
 * Get param with fallback to sessionStorage
 * Use this pattern to maintain params even if URL is lost
 */
export const getParamWithFallback = (paramName: string): string | null => {
  // First try URL
  const params = new URLSearchParams(window.location.search);
  const urlValue = params.get(paramName);
  
  if (urlValue) {
    // Cache to sessionStorage for fallback
    sessionStorage.setItem(paramName, urlValue);
    return urlValue;
  }
  
  // Fallback to sessionStorage
  return sessionStorage.getItem(paramName);
};
